import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ConsoleLogger, DefaultVideoTransformDevice, VideoFxProcessor } from 'amazon-chime-sdk-js'
import { useLocalVideo, useMeetingManager, useVideoInputs } from 'amazon-chime-sdk-component-library-react'
import { useGetEstablishmentBackgroundsQuery } from '@/core/services/establishment'
import { skipToken } from '@reduxjs/toolkit/query'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  getBackgroundEffectConfig,
  getBlurEffectConfig,
  getPreferenceBackgroundFromStorage,
  getVideoFxDefaultConfig,
  removePreferenceBackgroundFromStorage,
  savePreferenceBackgroundToStorage,
} from '../lib/backgroundHelpers'

const processingBudgetPerFrame = 50

export function useChimeBackground() {
  const { current: logger } = useRef(new ConsoleLogger('FilterLogger'))

  const authUser = useAppSelector(selectAuthUser)

  useIsVideoFxProcessorSupported(logger)
  const videoFxProcessor = useInitializeVideoFxProcessor(logger)
  usePreferenceBackground(videoFxProcessor)
  useVideoTransform(logger, videoFxProcessor)

  const toggleBlurEffect = useCallback(() => {
    if (!authUser) return

    if (!videoFxProcessor) {
      logger.warn('VideoFxProcessor not initialized')
      return
    }

    const {
      backgroundBlur: { isEnabled },
    } = videoFxProcessor.getEffectConfig()

    if (isEnabled) {
      videoFxProcessor.setEffectConfig(getVideoFxDefaultConfig())
      removePreferenceBackgroundFromStorage(authUser._id)
      return
    }

    videoFxProcessor.setEffectConfig(getBlurEffectConfig())
    savePreferenceBackgroundToStorage(authUser._id, 'blur')
  }, [videoFxProcessor])

  const toggleBackgroundEffect = useCallback(
    (imageUrl: string, imageKey: string) => {
      if (!authUser) return

      if (!videoFxProcessor) {
        logger.warn('VideoFxProcessor not initialized')
        return
      }

      const {
        backgroundReplacement: { backgroundImageURL: currentImageUrl },
      } = videoFxProcessor.getEffectConfig()

      if (currentImageUrl === imageUrl) {
        videoFxProcessor.setEffectConfig(getVideoFxDefaultConfig())
        removePreferenceBackgroundFromStorage(authUser._id)
        return
      }

      videoFxProcessor.setEffectConfig(getBackgroundEffectConfig(imageUrl))
      savePreferenceBackgroundToStorage(authUser._id, imageKey)
    },
    [videoFxProcessor]
  )

  return {
    effectConfig: videoFxProcessor?.getEffectConfig(),
    toggleBlurEffect,
    toggleBackgroundEffect,
  }
}

function useInitializeVideoFxProcessor(logger: ConsoleLogger) {
  const [videoFxProcessor, setVideoFxProcessor] = useState<VideoFxProcessor>()

  useEffect(() => {
    VideoFxProcessor.create(logger, getVideoFxDefaultConfig(), processingBudgetPerFrame)
      .then((processor) => {
        setVideoFxProcessor(processor)
      })
      .catch((error) => {
        logger.error(`Error initializing VideoFxProcessor: ${error}`)
        throw error
      })
  }, [logger])

  useEffect(() => {
    return () => {
      videoFxProcessor?.destroy()
    }
  }, [videoFxProcessor])

  return videoFxProcessor
}

function useIsVideoFxProcessorSupported(logger: ConsoleLogger) {
  const [isSupported, setIsSupported] = useState(false)

  useEffect(() => {
    VideoFxProcessor.isSupported()
      .then((supported) => {
        setIsSupported(supported)
      })
      .catch((error) => {
        logger.error(`VideoFxProcessor is not supported: ${error}`)
      })
  }, [logger])

  return isSupported
}

function usePreferenceBackground(videoFxProcessor?: VideoFxProcessor) {
  const authUser = useAppSelector(selectAuthUser)
  const establishmentId = authUser?.establishment?._id
  const { data: backgrounds } = useGetEstablishmentBackgroundsQuery(establishmentId ? { establishmentId } : skipToken)

  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (!authUser || isInitialized || !videoFxProcessor) return

    const storedPreferenceBackground = getPreferenceBackgroundFromStorage(authUser._id)
    const background = backgrounds?.find((background) => background.key === storedPreferenceBackground)
    if (background) {
      videoFxProcessor.setEffectConfig(getBackgroundEffectConfig(background.url))
      setIsInitialized(true)
      return
    }
    if (storedPreferenceBackground === 'blur') {
      videoFxProcessor.setEffectConfig(getBlurEffectConfig())
      setIsInitialized(true)
    }
  }, [authUser, backgrounds, videoFxProcessor])
}

function useVideoTransform(logger: ConsoleLogger, videoFxProcessor?: VideoFxProcessor) {
  const meetingManager = useMeetingManager()
  const { selectedDevice: selectedVideoInputDevice } = useVideoInputs()
  const { isVideoEnabled } = useLocalVideo()

  useEffect(() => {
    if (!meetingManager || !selectedVideoInputDevice || !isVideoEnabled || !videoFxProcessor) return

    const videoInputDevice = meetingManager.videoInputDevices?.find((device) => device.deviceId === selectedVideoInputDevice)
    if (!videoInputDevice) return

    const transformDevice = new DefaultVideoTransformDevice(logger, videoInputDevice, [videoFxProcessor])

    meetingManager.meetingSession?.audioVideo.startVideoInput(transformDevice).catch((error) => console.error('Error applying video effects: ', error))
  }, [meetingManager, selectedVideoInputDevice, isVideoEnabled, videoFxProcessor, logger])
}
